import { useMemo } from 'react'
import {
  groupAccommodationRatesByRefundability,
  type GroupedAccommodationRate,
} from 'lib/hotels/groupAccommodationRatesByRefundability'

export type GroupedPropertyRate = GroupedAccommodationRate<App.EnrichedPropertyRate>

/**
 * Groups Property rates by package type (boardCode) and inclusions.
 *
 * Strategy:
 * 1. Group rates by package type (boardCode) and inclusions (extracted from groupKey)
 * 2. Preserve all rates within each group (non-refundable, partially refundable, and fully refundable)
 * 3. Preserve original order from accommodation service (which sorts by LuxPlus+ price where available)
 * 4. Resort by refundability
 *
 * Uses accommodation service's groupKey for inclusion matching, which has more aggressive
 * normalization logic and coalesces meal-related inclusions together.
 *
 * @param rates - Array of property rates (with defaultOrder field from accommodation service)
 * @returns Grouped rates by package type and inclusions
 */

function useGroupedPropertyRatesByRefundability(
  rates: Array<App.EnrichedPropertyRate>,
): Array<GroupedPropertyRate> {
  return useMemo(() => groupAccommodationRatesByRefundability(rates, rate => ({
    rateId: rate.rateId,
    groupKey: rate.groupKey,
    boardCode: rate.boardCode,
    refundability: rate.headlineCancellationPolicyType,
  })), [rates])
}

export default useGroupedPropertyRatesByRefundability
